import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { 
	PaymentMilestone, 
	PaymentMilestoneCreateDto, 
	PaymentMilestoneStage, 
	PaymentMilestoneStageCreateDto,
	PaymentMilestoneResponse,
	PaymentMilestoneStageResponse
} from "../../modules/shared/model";
import { 
	createPaymentMilestone as createPaymentMilestoneAction, 
	updatePaymentMilestone as updatePaymentMilestoneAction, 
	getPaymentMilestoneById as getPaymentMilestoneByIdAction, 
	getPaymentMilestones as getPaymentMilestonesAction,
	deletePaymentMilestone as deletePaymentMilestoneAction,
	createPaymentMilestoneStage as createPaymentMilestoneStageAction,
	updatePaymentMilestoneStage as updatePaymentMilestoneStageAction,
	getPaymentMilestoneStageById as getPaymentMilestoneStageByIdAction,
	getPaymentMilestoneStages as getPaymentMilestoneStagesAction,
	getPaymentMilestoneStagesByMilestone as getPaymentMilestoneStagesByMilestoneAction,
	deletePaymentMilestoneStage as deletePaymentMilestoneStageAction
} from "../Actions/PaymentMilestoneActions";

// Payment Milestones Async Thunks
export const createPaymentMilestone = createAsyncThunk("paymentMilestones/create", async (data: PaymentMilestoneCreateDto, { rejectWithValue }) => {
	try { return await createPaymentMilestoneAction(data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to create payment milestone"); }
});

export const updatePaymentMilestone = createAsyncThunk("paymentMilestones/update", async ({ id, data }: { id: number; data: Partial<PaymentMilestoneCreateDto> }, { rejectWithValue }) => {
	try { return await updatePaymentMilestoneAction(id, data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to update payment milestone"); }
});

export const fetchPaymentMilestone = createAsyncThunk("paymentMilestones/getById", async (id: number, { rejectWithValue }) => {
	try { return await getPaymentMilestoneByIdAction(id); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch payment milestone"); }
});

export const fetchPaymentMilestones = createAsyncThunk("paymentMilestones/get", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
	try { return await getPaymentMilestonesAction(query); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch payment milestones"); }
});


export const deletePaymentMilestone = createAsyncThunk("paymentMilestones/delete", async (id: number, { rejectWithValue }) => {
	try { await deletePaymentMilestoneAction(id); return id; } catch (e: any) { return rejectWithValue(e?.message || "Failed to delete payment milestone"); }
});

// Payment Milestone Stages Async Thunks
export const createPaymentMilestoneStage = createAsyncThunk("paymentMilestoneStages/create", async (data: PaymentMilestoneStageCreateDto, { rejectWithValue }) => {
	try { return await createPaymentMilestoneStageAction(data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to create payment milestone stage"); }
});

export const updatePaymentMilestoneStage = createAsyncThunk("paymentMilestoneStages/update", async ({ id, data }: { id: number; data: Partial<PaymentMilestoneStageCreateDto> }, { rejectWithValue }) => {
	try { return await updatePaymentMilestoneStageAction(id, data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to update payment milestone stage"); }
});

export const fetchPaymentMilestoneStage = createAsyncThunk("paymentMilestoneStages/getById", async (id: number, { rejectWithValue }) => {
	try { return await getPaymentMilestoneStageByIdAction(id); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch payment milestone stage"); }
});

export const fetchPaymentMilestoneStages = createAsyncThunk("paymentMilestoneStages/get", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
	try { return await getPaymentMilestoneStagesAction(query); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch payment milestone stages"); }
});

export const fetchPaymentMilestoneStagesByMilestone = createAsyncThunk("paymentMilestoneStages/getByMilestone", async (milestoneId: number, { rejectWithValue }) => {
	try { return await getPaymentMilestoneStagesByMilestoneAction(milestoneId); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch payment milestone stages by milestone"); }
});

export const deletePaymentMilestoneStage = createAsyncThunk("paymentMilestoneStages/delete", async (id: number, { rejectWithValue }) => {
	try { await deletePaymentMilestoneStageAction(id); return id; } catch (e: any) { return rejectWithValue(e?.message || "Failed to delete payment milestone stage"); }
});

interface PaymentMilestoneState {
	// Payment Milestones
	milestones: PaymentMilestone[];
	currentMilestone: PaymentMilestone | null;
	milestonesLoading: boolean;
	milestonesError: string | null;
	milestonesTotalCount?: number;
	milestonesHasNextPage?: boolean;
	milestonesHasPreviousPage?: boolean;
	milestonesCurrentPage?: number;
	milestonesLimit?: number;
	// Payment Milestone Stages
	stages: PaymentMilestoneStage[];
	currentStage: PaymentMilestoneStage | null;
	stagesLoading: boolean;
	stagesError: string | null;
	stagesTotalCount?: number;
}

const initialState: PaymentMilestoneState = {
	// Payment Milestones
	milestones: [],
	currentMilestone: null,
	milestonesLoading: false,
	milestonesError: null,
	milestonesTotalCount: 0,
	milestonesHasNextPage: false,
	milestonesHasPreviousPage: false,
	milestonesCurrentPage: 1,
	milestonesLimit: 10,
	// Payment Milestone Stages
	stages: [],
	currentStage: null,
	stagesLoading: false,
	stagesError: null,
	stagesTotalCount: 0,
};

const paymentMilestoneSlice = createSlice({
	name: "paymentMilestones",
	initialState,
	reducers: {
		clearPaymentMilestonesError(state) { state.milestonesError = null; },
		clearCurrentPaymentMilestone(state) { state.currentMilestone = null; },
		clearPaymentMilestoneStagesError(state) { state.stagesError = null; },
		clearCurrentPaymentMilestoneStage(state) { state.currentStage = null; },
		clearPaymentMilestoneStages(state) {
			state.stages = [];
			state.currentStage = null;
			state.stagesTotalCount = 0;
		}
	},
	extraReducers: (builder) => {
		// Payment Milestones Extra Reducers
		builder
			.addCase(createPaymentMilestone.pending, (s) => { s.milestonesLoading = true; s.milestonesError = null; })
			.addCase(createPaymentMilestone.fulfilled, (s, a: PayloadAction<PaymentMilestone>) => { s.milestonesLoading = false; s.milestones.unshift(a.payload); s.milestonesTotalCount = (s.milestonesTotalCount || 0) + 1; })
			.addCase(createPaymentMilestone.rejected, (s, a: any) => { s.milestonesLoading = false; s.milestonesError = a.payload as string; });

		builder
			.addCase(updatePaymentMilestone.pending, (s) => { s.milestonesLoading = true; s.milestonesError = null; })
			.addCase(updatePaymentMilestone.fulfilled, (s, a: PayloadAction<PaymentMilestone>) => {
				s.milestonesLoading = false;
				const i = s.milestones.findIndex(x => x.id === a.payload.id);
				if (i !== -1) s.milestones[i] = a.payload;
				if (s.currentMilestone?.id === a.payload.id) s.currentMilestone = a.payload;
			})
			.addCase(updatePaymentMilestone.rejected, (s, a: any) => { s.milestonesLoading = false; s.milestonesError = a.payload as string; });

		builder
			.addCase(fetchPaymentMilestone.pending, (s) => { s.milestonesLoading = true; s.milestonesError = null; })
			.addCase(fetchPaymentMilestone.fulfilled, (s, a: PayloadAction<PaymentMilestone>) => { s.milestonesLoading = false; s.currentMilestone = a.payload; })
			.addCase(fetchPaymentMilestone.rejected, (s, a: any) => { s.milestonesLoading = false; s.milestonesError = a.payload as string; });

		builder
			.addCase(fetchPaymentMilestones.pending, (s) => { s.milestonesLoading = true; s.milestonesError = null; })
			.addCase(fetchPaymentMilestones.fulfilled, (s, a: PayloadAction<PaymentMilestoneResponse>) => { 
				console.log("PaymentMilestoneSlice: Received response:", a.payload);
				s.milestonesLoading = false; 
				s.milestones = a.payload.data || []; 
				s.milestonesTotalCount = a.payload.totalCount || 0;
				s.milestonesHasNextPage = a.payload.hasNextPage || false;
				s.milestonesHasPreviousPage = a.payload.hasPreviousPage || false;
				s.milestonesCurrentPage = a.payload.page || 1;
				s.milestonesLimit = a.payload.limit || 10;
				console.log("PaymentMilestoneSlice: Updated state:", {
					milestones: s.milestones.length,
					totalCount: s.milestonesTotalCount,
					hasNextPage: s.milestonesHasNextPage,
					hasPreviousPage: s.milestonesHasPreviousPage,
					currentPage: s.milestonesCurrentPage,
					limit: s.milestonesLimit
				});
			})
			.addCase(fetchPaymentMilestones.rejected, (s, a: any) => { s.milestonesLoading = false; s.milestonesError = a.payload as string; });


		builder
			.addCase(deletePaymentMilestone.pending, (s) => { s.milestonesLoading = true; s.milestonesError = null; })
			.addCase(deletePaymentMilestone.fulfilled, (s, a: PayloadAction<number>) => { s.milestonesLoading = false; s.milestones = s.milestones.filter(x => x.id !== a.payload); if (s.currentMilestone?.id === a.payload) s.currentMilestone = null; s.milestonesTotalCount = Math.max(0, (s.milestonesTotalCount || 0) - 1); })
			.addCase(deletePaymentMilestone.rejected, (s, a: any) => { s.milestonesLoading = false; s.milestonesError = a.payload as string; });

		// Payment Milestone Stages Extra Reducers
		builder
			.addCase(createPaymentMilestoneStage.pending, (s) => { s.stagesLoading = true; s.stagesError = null; })
			.addCase(createPaymentMilestoneStage.fulfilled, (s, a: PayloadAction<PaymentMilestoneStage>) => { s.stagesLoading = false; s.stages.unshift(a.payload); s.stagesTotalCount = (s.stagesTotalCount || 0) + 1; })
			.addCase(createPaymentMilestoneStage.rejected, (s, a: any) => { s.stagesLoading = false; s.stagesError = a.payload as string; });

		builder
			.addCase(updatePaymentMilestoneStage.pending, (s) => { s.stagesLoading = true; s.stagesError = null; })
			.addCase(updatePaymentMilestoneStage.fulfilled, (s, a: PayloadAction<PaymentMilestoneStage>) => {
				s.stagesLoading = false;
				const i = s.stages.findIndex(x => x.id === a.payload.id);
				if (i !== -1) s.stages[i] = a.payload;
				if (s.currentStage?.id === a.payload.id) s.currentStage = a.payload;
			})
			.addCase(updatePaymentMilestoneStage.rejected, (s, a: any) => { s.stagesLoading = false; s.stagesError = a.payload as string; });

		builder
			.addCase(fetchPaymentMilestoneStage.pending, (s) => { s.stagesLoading = true; s.stagesError = null; })
			.addCase(fetchPaymentMilestoneStage.fulfilled, (s, a: PayloadAction<PaymentMilestoneStage>) => { s.stagesLoading = false; s.currentStage = a.payload; })
			.addCase(fetchPaymentMilestoneStage.rejected, (s, a: any) => { s.stagesLoading = false; s.stagesError = a.payload as string; });

		builder
			.addCase(fetchPaymentMilestoneStages.pending, (s) => { s.stagesLoading = true; s.stagesError = null; })
			.addCase(fetchPaymentMilestoneStages.fulfilled, (s, a: PayloadAction<PaymentMilestoneStageResponse>) => { s.stagesLoading = false; s.stages = a.payload.data || []; s.stagesTotalCount = a.payload.totalCount || 0; })
			.addCase(fetchPaymentMilestoneStages.rejected, (s, a: any) => { s.stagesLoading = false; s.stagesError = a.payload as string; });

		builder
			.addCase(fetchPaymentMilestoneStagesByMilestone.pending, (s) => { s.stagesLoading = true; s.stagesError = null; })
			.addCase(fetchPaymentMilestoneStagesByMilestone.fulfilled, (s, a: PayloadAction<PaymentMilestoneStage[]>) => { s.stagesLoading = false; s.stages = a.payload || []; })
			.addCase(fetchPaymentMilestoneStagesByMilestone.rejected, (s, a: any) => { s.stagesLoading = false; s.stagesError = a.payload as string; });

		builder
			.addCase(deletePaymentMilestoneStage.pending, (s) => { s.stagesLoading = true; s.stagesError = null; })
			.addCase(deletePaymentMilestoneStage.fulfilled, (s, a: PayloadAction<number>) => { s.stagesLoading = false; s.stages = s.stages.filter(x => x.id !== a.payload); if (s.currentStage?.id === a.payload) s.currentStage = null; s.stagesTotalCount = Math.max(0, (s.stagesTotalCount || 0) - 1); })
			.addCase(deletePaymentMilestoneStage.rejected, (s, a: any) => { s.stagesLoading = false; s.stagesError = a.payload as string; });
	}
});

export const { 
	clearPaymentMilestonesError, 
	clearCurrentPaymentMilestone, 
	clearPaymentMilestoneStagesError,
	clearCurrentPaymentMilestoneStage,
	clearPaymentMilestoneStages
} = paymentMilestoneSlice.actions;
export default paymentMilestoneSlice.reducer;
